function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Editor, EditorState, RichUtils, CompositeDecorator, AtomicBlockUtils, convertFromRaw, convertToRaw } from "draft-js";
import Stack from "@churchofjesuschrist/eden-stack";
import Controls from "./Controls";
import Link from "./Link";
import Image from "./Image";
import LinkPrompt from "./LinkPrompt";
import ImagePrompt from "./ImagePrompt";
import { customBlockStyleRendering, EditorStyleSideEffects, Hint, HiddenInput } from "./EditorStyleSideEffects";
const RichTextEditor = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    onChange,
    initialContent,
    availableControls,
    placeholder,
    spellCheck,
    removePastedStyles,
    editorKey,
    maxLength,
    name,
    required,
    disabled,
    strings,
    ...rest
  } = _ref;
  const decorator = new CompositeDecorator([{
    strategy: (contentBlock, callback, contentState) => {
      contentBlock.findEntityRanges(character => {
        const entityKey = character.getEntity();
        return entityKey !== null && contentState.getEntity(entityKey).getType() === "LINK";
      }, callback);
    },
    component: Link
  }, {
    strategy: (contentBlock, callback, contentState) => {
      contentBlock.findEntityRanges(character => {
        const entityKey = character.getEntity();
        return entityKey !== null && contentState.getEntity(entityKey).getType() === "IMAGE";
      }, callback);
    },
    component: Image
  }]);
  const [editorState, setEditorState] = useState(() => {
    if (initialContent) {
      const initialEditorState = EditorState.createWithContent(convertFromRaw(initialContent), decorator);
      return initialEditorState;
    } else {
      const initialEditorState = EditorState.createWithContent(convertFromRaw({
        entityMap: {},
        blocks: [{
          text: "",
          key: "foo",
          type: "unstyled",
          entityRanges: []
        }]
      }), decorator);
      return initialEditorState;
    }
  });
  useEffect(() => {
    if (onChange) {
      const contentState = editorState.getCurrentContent();
      const content = convertToRaw(contentState);
      onChange(content);
    }
  }, [onChange, editorState]);
  const [editor, setEditor] = useState(initialContent ? false : true);
  useEffect(() => {
    setEditor(true);
  }, []);
  const [showLinkPrompt, setShowLinkPrompt] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [showImagePrompt, setShowImagePrompt] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageDescription, setImageDescription] = useState("");
  const [text, setText] = useState(editorState.getCurrentContent().getPlainText("\u0001"));
  const [isInvalid, setIsInvalid] = useState(false);
  const internalRef = useRef(null);

  const focus = () => {
    const usedRef = ref ? ref.current : internalRef.current;
    usedRef.focus();
  };

  const onInlineTypeToggle = inlineType => {
    focus();
    setEditorState(state => RichUtils.toggleInlineStyle(state, inlineType));
  };

  const onBlockTypeToggle = blockType => {
    focus();
    setEditorState(state => RichUtils.toggleBlockType(state, blockType));
  };

  const handleInvalid = () => setIsInvalid(true);

  return /*#__PURE__*/React.createElement("div", rest, editor && /*#__PURE__*/React.createElement(Stack, {
    gapSize: "8"
  }, /*#__PURE__*/React.createElement(Controls, {
    disabled: disabled,
    availableControls: availableControls,
    isInlineTypeActive: inlineType => {
      return editorState.getCurrentInlineStyle().has(inlineType);
    },
    onInlineTypeToggle: onInlineTypeToggle,
    isBlockTypeActive: blockType => {
      return blockType === editorState.getCurrentContent().getBlockForKey(editorState.getSelection().getStartKey()).getType();
    },
    onBlockTypeToggle: onBlockTypeToggle,
    promptForLink: () => {
      const selection = editorState.getSelection();

      if (!selection.isCollapsed()) {
        const contentState = editorState.getCurrentContent();
        const startKey = editorState.getSelection().getStartKey();
        const startOffset = editorState.getSelection().getStartOffset();
        const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
        const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
        let url = "";

        if (linkKey) {
          const linkInstance = contentState.getEntity(linkKey);
          url = linkInstance.getData().url;
        }

        setLinkUrl(url);
        setShowLinkPrompt(true);
      }
    },
    promptForImage: () => {
      setImageUrl("");
      setImageDescription("");
      setShowImagePrompt(true);
    },
    strings: strings
  }), showLinkPrompt ? /*#__PURE__*/React.createElement(LinkPrompt, {
    url: linkUrl,
    onUrlChange: url => setLinkUrl(url),
    onConfirm: () => {
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity("LINK", "MUTABLE", {
        url: linkUrl
      });
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const newEditorState = EditorState.set(editorState, {
        currentContent: contentStateWithEntity
      });
      setEditorState(RichUtils.toggleLink(newEditorState, newEditorState.getSelection(), entityKey));
      setShowLinkPrompt(false);
      setLinkUrl("");
    },
    onCancel: () => {
      setShowLinkPrompt(false);
    },
    strings: strings
  }) : null, showImagePrompt ? /*#__PURE__*/React.createElement(ImagePrompt, {
    url: imageUrl,
    onUrlChange: url => setImageUrl(url),
    description: imageDescription,
    onDescriptionChange: description => setImageDescription(description),
    onConfirm: () => {
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity("IMAGE", "MUTABLE", {
        src: imageUrl,
        alt: imageDescription
      });
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const newEditorState = EditorState.set(editorState, {
        currentContent: contentStateWithEntity
      });
      setEditorState(AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, " "));
      setShowImagePrompt(false);
      setImageUrl("");
    },
    onCancel: () => {
      setShowImagePrompt(false);
    },
    strings: strings
  }) : null, /*#__PURE__*/React.createElement(EditorStyleSideEffects, {
    validityState: maxLength && text.length > parseInt(maxLength, 10) || required && isInvalid && text.length === 0 ? "error" : "unknown",
    disabled: disabled,
    onClick: focus
  }, /*#__PURE__*/React.createElement(Editor, {
    editorState: editorState,
    blockStyleFn: customBlockStyleRendering,
    handleKeyCommand: (command, editorState) => {
      const newState = RichUtils.handleKeyCommand(editorState, command);

      if (newState) {
        setEditorState(newState);
        return true;
      }

      return false;
    },
    onChange: editorState => {
      setIsInvalid(false);
      setText(editorState.getCurrentContent().getPlainText("\u0001"));
      setEditorState(editorState);
    },
    placeholder: placeholder,
    spellCheck: spellCheck,
    stripPastedStyles: removePastedStyles,
    editorKey: editorKey,
    ref: ref ? ref : internalRef,
    readOnly: disabled
  }), /*#__PURE__*/React.createElement(HiddenInput, _extends({
    tabIndex: "-1",
    value: text,
    name: name,
    required: required
  }, maxLength ? {
    pattern: `.{0,${maxLength}}`
  } : {}, {
    onChange: x => x,
    onFocus: focus,
    disabled: disabled,
    onInvalid: handleInvalid
  }))), maxLength || required ? /*#__PURE__*/React.createElement(Hint, {
    validityState: maxLength && text.length > parseInt(maxLength, 10) || required && isInvalid && text.length === 0 ? "error" : "unknown"
  }, required && isInvalid && text.length === 0 ? strings.required : maxLength ? `${text.length > parseInt(maxLength, 10) ? strings.charactersOver : strings.charactersLeft}:
                ${Math.abs(text.length - parseInt(maxLength, 10))}` : "") : null));
});
RichTextEditor.defaultProps = {
  availableControls: ["heading1", "heading2", "heading3", "heading4", "heading5", "heading6", "preformatted", "bold", "italic", "underline", "bulletedList", "numberedList", "blockquote", "link", "image"],
  placeholder: "And it came to pass...",
  spellCheck: false,
  removePastedStyles: false,
  editorKey: "eden-rich-text-editor",
  strings: {
    paragraph: "Paragraph",
    heading1: "Heading 1",
    heading2: "Heading 2",
    heading3: "Heading 3",
    heading4: "Heading 4",
    heading5: "Heading 5",
    heading6: "Heading 6",
    preformatted: "Preformatted",
    bold: "Bold",
    italic: "Italic",
    underline: "Underline",
    bulletedList: "Bulleted List",
    numberedList: "Numbered List",
    blockquote: "Quote",
    link: "Link",
    image: "Image",
    confirm: "Confirm",
    cancel: "Cancel",
    url: "URL",
    description: "Description",
    charactersLeft: "Characters Left",
    charactersOver: "Characters Over",
    required: "Required"
  }
};
RichTextEditor.propTypes = {
  /** Callback to execute with the current content of the editor when the editor contents change, including when initialized */
  onChange: PropTypes.func,

  /** Content for the editor to initialize with */
  initialContent: PropTypes.object,

  /** Enabled rich text controls */
  availableControls: PropTypes.array,

  /** Text that appears within the editor when empty */
  placeholder: PropTypes.string,

  /** Whether the editor should include [spell checking](https://developer.mozilla.org/en-US/docs/Mozilla/Tech/XUL/Attribute/spellcheck) */
  spellCheck: PropTypes.bool,

  /** Whether the editor should remove all information except plaintext from pasted content */
  removePastedStyles: PropTypes.bool,

  /** A unique ID if there are multiple editors used on the same page */
  editorKey: PropTypes.string,

  /** The maximum allowed characters. See [maxlength](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/textarea#attr-maxlength) */
  maxLength: PropTypes.string,

  /** The name of the editor. */
  name: PropTypes.string,

  /** Whether or not the editor must be filled in */
  required: PropTypes.bool,

  /** Whether or not the editor is usable */
  disabled: PropTypes.bool,

  /** Translation strings */
  strings: PropTypes.shape({
    paragraph: PropTypes.string.isRequired,
    heading1: PropTypes.string.isRequired,
    heading2: PropTypes.string.isRequired,
    heading3: PropTypes.string.isRequired,
    heading4: PropTypes.string.isRequired,
    heading5: PropTypes.string.isRequired,
    heading6: PropTypes.string.isRequired,
    preformatted: PropTypes.string.isRequired,
    bold: PropTypes.string.isRequired,
    italic: PropTypes.string.isRequired,
    underline: PropTypes.string.isRequired,
    bulletedList: PropTypes.string.isRequired,
    numberedList: PropTypes.string.isRequired,
    blockquote: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    confirm: PropTypes.string.isRequired,
    cancel: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    charactersLeft: PropTypes.string.isRequired,
    charactersOver: PropTypes.string.isRequired,
    required: PropTypes.string.isRequired
  })
};
RichTextEditor.displayName = "RichTextEditor";
export default RichTextEditor;